/**
 * How a surface offers to *show* an entity rather than navigate to it.
 *
 * The agent workspaces mount an artifact panel on the right and provide an
 * opener here; entity citations in prose, tool result rows and message
 * actions ask for it and, when the kind has a viewer, open the panel instead
 * of leaving the page. Where nothing provides an opener (the side panel, the
 * Case IDE) the same components fall back to navigating.
 *
 * Plain data and a context; the panel itself lives in `workspace/`.
 */
import { createContext, useContext } from "react";

import type {
  EntityKind,
  EntityRefTarget,
  RecordFocusKey
} from "../refs/entityRefs";
import { kindLabel, splitRefTitle } from "../refs/entityRefs";

/** Everything the panel can show: entities, plus a message read as a report. */
export type ArtifactKind = EntityKind | "report";

export type ArtifactTarget = {
  kind: ArtifactKind;
  /** The entity's id; the message id for a report; the scope id for a client / practice. */
  id: string;
  /** What to call it in the panel header. */
  label: string;
  /** The `Kind · status · …` detail from the link title, minus the kind. */
  detail?: string | null;
  /** App path for "Open in app"; null when the artifact has no page (a report). */
  path: string | null;
  clientId?: string;
  individualId?: string;
  focusKey?: RecordFocusKey;
  /** Report body (markdown). */
  markdown?: string;
};

export type ArtifactOpener = {
  open: (target: ArtifactTarget) => void;
  /** Whether the panel has a viewer for this kind (otherwise callers navigate). */
  canOpen: (kind: ArtifactKind) => boolean;
};

const ArtifactOpenerContext = createContext<ArtifactOpener | null>(null);

export const ArtifactOpenerProvider = ArtifactOpenerContext.Provider;

/** The surrounding surface's opener, or null when there is none. */
export const useArtifactOpener = (): ArtifactOpener | null =>
  useContext(ArtifactOpenerContext);

/** An entity link (`[label](path "title")`) as something the panel can show. */
export const targetFromRef = (
  ref: EntityRefTarget,
  label: string,
  title?: string | null
): ArtifactTarget => {
  const { detail } = splitRefTitle(title);
  const id =
    ref.id ??
    (ref.kind === "client" ? ref.clientId : undefined) ??
    (ref.kind === "case" ? ref.individualId : undefined) ??
    ref.path;
  return {
    kind: ref.kind,
    id,
    label: label || kindLabel(ref),
    detail,
    path: ref.path,
    clientId: ref.clientId,
    individualId: ref.individualId,
    focusKey: ref.focusKey
  };
};
